import styles from '@/styles/recruiter.module.scss';
import Layout from '@/layouts/page-base.layout';
import type { GetServerSidePropsContext } from 'next';
import type { THr, TFooter, OrganizationEntityParams } from '@/types/recruiter';
import { useEffect, useState } from 'react';
import Card from '@/components/position/card';
import Link from 'next/link';
import { openApp, getLocalesEnv } from '@/utils';
import { Ellipsis } from 'antd-mobile';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { WEB3_EVENT_ID, WEB3_EVENT_OPERATE } from '@/consts';
import track from '@/utils/track';
import channel from '@/utils/channel';
import Cookies from 'js-cookie';

import compLogImg from '@/static/images/landing/company-log.jpg';
import chat from '@/static/images/landing/chat.png';
import arrowRighGrey from '@/static/images/landing/arrow-right_grey.svg';
import successFilled from '@/static/images/landing/success-filled.svg';

import ServerAPI from '@/api/server-api';
import clientAPI from '@/api/client-api';
import useOpenInstall from '@/hooks/useOpenInstall';
import useChannel from '@/hooks/useChannel';
import clientUrlHelper from '@/utils/client-url';
import Head from '@/components/headv2'
import dynamic from 'next/dynamic';
import { ShareConfig } from '@/types/head';

const WeChat = dynamic(import('@/components/wx'), { ssr: false })

function OrganizationInfo({ organizationDetail }: any) {
  const { uuid8,
    organizationLogoUrl,
    verifyStatus,
    organizationName,
    shortDescription,
    financingStageStr,
    organizationSizeStr,
    industryStr } = organizationDetail

  const { t } = useTranslation('common');
  const expandText: string = t('VIEWALL');
  const [showMore, setShowMore] = useState(true);

  return (
    <div className={styles['employer']}>
      <h3 className={`${styles['h3']}`}>{t('Employer')}</h3>

      <a href={`/project/${uuid8}`}>
        <div className={styles['employer-com']}>
          <div
            className={`${styles['employer-logo']}`}
            style={{
              backgroundImage: `url(${organizationLogoUrl || compLogImg.src})`,
            }}
          ></div>
          <div className={styles['employer-info']}>
            <div className={styles['inline-wrap']}>
              <div className={styles['block-wrap']}>
                <div className={styles['employer-name']}>
                  {verifyStatus === 1 ? (
                    <div
                      className={`${styles['success-filled']}`}
                      style={{ backgroundImage: `url(${successFilled.src})` }}
                    ></div>
                  ) : (
                    ''
                  )}
                  {organizationName}
                </div>
              </div>
            </div>

            <div className={styles['employer-base']}>
              {financingStageStr ? <span>{financingStageStr}</span> : ''}
              {organizationSizeStr ? <span> · {organizationSizeStr}</span> : ''}
              {industryStr ? <span> · {industryStr}</span> : ''}
            </div>
          </div>

          <div
            className={`${styles['arrow-righ']}`}
            style={{ backgroundImage: `url(${arrowRighGrey.src})` }}
          ></div>
        </div>
      </a>

      <div className={styles['employer-desc']}>
        <Ellipsis
          rows={5}
          content={shortDescription}
          direction="end"
          expandText={expandText}
        />
      </div>
    </div>
  );
}

function EmployerInfo({ employerDetail }: any) {
  return (
    <div className={styles['hr']}>
      <div className={`${styles['hr-avatar-wrap']}`}>
        <div
          className={`${styles['hr-avatar']}`}
          style={{ backgroundImage: `url(${employerDetail.formalPhotoUrl})` }}
        />

        <div className={styles['hr-online']}></div>
      </div>

      <div className={styles['hr-name']}>{employerDetail.realName}</div>

      <div className={styles['hr-desc']}>
        {employerDetail.organizationName} · {employerDetail.currentTitle}
      </div>
    </div>
  )
}

function PositionList({ list }: { list: any[] }) {
  const { t } = useTranslation('common');

  return (
    <div className={styles['job']}>
      <h3 className={`${styles['h3']} relative`}>
        {t('JobPosted')}
        <span className={styles['job-count']}>{list.length}</span>
      </h3>

      <ul className={styles['job-list']}>
        {list.map((item, index) => (
          <Card {...item} key={item.id} />
        ))}
      </ul>
    </div>
  );
}

function Footer({ avatar, onClick }: any) {
  const { t } = useTranslation('common');

  return (
    <footer className={`${styles.footer} fixed bottom-0 w-full footer`}>
      <div className={styles['footer-content']}>
        <div
          className={`${styles.avatar} relative`}
          style={{ backgroundImage: `url(${avatar})` }}
        >
          <div
            className={`${styles.chat}`}
            style={{ backgroundImage: `url(${chat.src})` }}
          ></div>
        </div>

        <div className={`${styles['footer-text']}`}>
          {t('GoToTheAPPAnd')}
          <span>{t('TalkToMeDirectlyAboutJob')}</span>
        </div>

        <button className={styles.btn} onClick={onClick}>
          {t('OPENAPP')}
        </button>
      </div>
    </footer>
  );
}

export async function getServerSideProps(props: GetServerSidePropsContext) {
  const start = new Date().getTime();
  const { query, params, req, res } = props

  const Language = getLocalesEnv(req, query)
  const i18n = await serverSideTranslations(Language, ['common'])
  const API = new ServerAPI({ acceptLanguage: Language })

  // 获取参数，并参数检查
  const organizationId = query?.oid as string
  const id = params?.id as string
  if (!organizationId || !id) return { notFound: true };

  // 这里后端的设计是，一个招聘方可能会属于多个公司，所以这里需要传入公司ID
  const [employer, organization, position] = await Promise.all([
    API.landing.getAccount(organizationId, id),
    API.landing.getOrganization(organizationId),
    API.landing.getAccountPosition(organizationId, id, { current: 1, size: 30 }),
  ])

  const employerDetail = employer.data
  const organizationDetail = organization.data
  const positionList = position.data.positions

  if (!employerDetail || !organizationDetail) return { notFound: true };

  const end = new Date().getTime();
  res.setHeader('X-TIME-SPAN', end - start);

  return {
    props: {
      params,
      query,
      employerDetail,
      organizationDetail,
      positionList,
      ...i18n,
    },
  };
}

export default function Page(props: any) {
  const { positionList, employerDetail, organizationDetail, query, params } = props;
  const channel = useChannel()
  const openinstall = useOpenInstall()
  const clientUrl = clientUrlHelper.recruiterDetailUrl(params.id)
  const appUrl = employerDetail.share.h5.default.universeUrl
  const share = employerDetail.share.h5
  const positionTitles = positionList.map((item: any) => item.positionTitle);

  useEffect(() => {
    handleActivityTask(query)
  }, []);

  function handleActivityTask(query: any) {
    if (!Cookies.get('WEB3_EVENT_RECRUITER')) {
      Cookies.set('WEB3_EVENT_RECRUITER', WEB3_EVENT_ID);
      clientAPI.events.setOrganizationPopular(WEB3_EVENT_ID, {
        score: WEB3_EVENT_OPERATE.recruiter.score,
        organizationUuid8: organizationDetail.uuid8,
        operateType: WEB3_EVENT_OPERATE.recruiter.type,
        openId: '',
      });
    }
  }

  const handleClickToApp = () => {
    track.click('footer_open_app_click', {channelCode: channel?.channel, shareCode: channel?.shareCode})
    // openinstall?.wakeupOrInstall({channelCode: channel?.channel, data: { path: clientUrl, shareCode: channel?.shareCode } })
    openApp(appUrl)
  }

  const seo = {
    title: `[SmartDeer] ${organizationDetail.organizationName}·${employerDetail.realName} is hiring for: ${positionTitles.join(' | ')}`,
    description: `Chat directly with Global Web3 Jobs.`,
    keywords: ''
  }

  const wechatShareConfig:ShareConfig = {
    title: `【SmartDeer】${organizationDetail.organizationName}·${employerDetail.realName}正在热招${positionTitles.length}个职位：${positionTitles.join('、')}`,
    description: '一键直聊全球Web3工作机会。',
    image: employerDetail.formalPhotoUrl,
    thumbnail: employerDetail.formalPhotoUrl,
    url: share.default.shareUrl
  }

  return (
    <Layout pagename='share_recruiter'>
      {/* head meta */}
      <Head seo={seo} share={share}></Head>

      {/* wechat share */}
      <WeChat share={wechatShareConfig} />

      <EmployerInfo employerDetail={employerDetail}></EmployerInfo>
      <div className={styles['main']}>
        <OrganizationInfo organizationDetail={organizationDetail} />
        <PositionList list={positionList} />
      </div>
      <Footer avatar={employerDetail.formalPhotoUrl} onClick={handleClickToApp} />
    </Layout>
  );
}
